import { defineStore } from "pinia";
import { ItemType, TagsApi } from "@simbelapp/order-sdk";
import { useAuthStore } from "../auth/auth.store";
import { useCreateCompaniesStore } from "./create-company.store";
import type { ICompanyTagsStore } from "~/utils/interfaces/companies-interfaces";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { TagCategoryEnum } from "~/utils/enums/tags.enums";

export const useCompanyTagsStore = defineStore("useCompanyTagsStore", {
  state: (): ICompanyTagsStore => {
    return {
      familiesTag: [],
      trainingTags: [],
      sessionTags: [],
      showDeleteTagModal: false,
    };
  },
  actions: {
    /** Fetch training tags for the current (or given) company. */
    async getTrainingTags({
      companyId,
      onlyUsed,
      itemType,
      nullable,
      onlyCatalogFilterVisible,
    }: {
      companyId?: string;
      onlyUsed?: string;
      itemType?: ItemType;
      nullable?: boolean;
      onlyCatalogFilterVisible?: string;
    }) {
      const companiesStore = useCreateCompaniesStore();
      if (companiesStore.company.companyId !== undefined || companyId !== undefined) {
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TagsApi);
          const trainingTag = await apiInstance.getCompanyTags({
            companyId: companiesStore.company.companyId ?? (companyId as string),
            category: TagCategoryEnum.TRAINING_TAG,
            onlyUsed,
            itemType,
            onlyCatalogFilterVisible,
          });
          this.trainingTags = nullable ? trainingTag.filter((e) => e !== null) : trainingTag;
          return trainingTag;
        } catch {
          useFeedback().error("Erreur lors de la récupération des tags de formation", "small");
        }
      }
    },

    /** Fetch session tags for the current (or given) company. */
    async getSessionTags(companyId?: string, nullable?: boolean) {
      const companiesStore = useCreateCompaniesStore();
      if (companiesStore.company.companyId !== undefined || companyId !== undefined) {
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TagsApi);
          const sessionTag = await apiInstance.getCompanyTags({
            companyId: companiesStore.company.companyId ?? (companyId as string),
            category: TagCategoryEnum.SESSION_TAG,
          });
          this.sessionTags = nullable ? sessionTag.filter((e) => e !== null) : sessionTag;
        } catch {
          useFeedback().error("Erreur lors de la récupération des tags de session", "small");
        }
      }
    },

    /** Fetch all tag families. */
    async getFamiliesTag() {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TagsApi);
        const response = await apiInstance.getTagFamilies();
        this.familiesTag = response;
        return response;
      } catch {
        useFeedback().error("Erreur lors de la récupération des familles de tags", "small");
      }
    },

    /** Delete a tag family, then refresh families and training tags. */
    async deleteTagFamily(id: string) {
      const authStore = useAuthStore();
      const mainEntityId = authStore.user?.mainEntityId;
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TagsApi);
        const response = await apiInstance.deleteTagFamily({
          tagFamilyId: id,
        });
        await this.getFamiliesTag();
        await this.getTrainingTags({ companyId: mainEntityId?.companyId, nullable: true });
        return response;
      } catch {
        useFeedback().error("Erreur lors de la suppression de la famille de tags", "small");
      }
    },

    /** Create a tag family, then refresh families. */
    async createTagFamily(name?: string) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TagsApi);
        const response = await apiInstance.createTagFamily({
          updateTagFamilyDto: {
            tagFamilyName: name || null,
          },
        });
        await this.getFamiliesTag();
        return response;
      } catch {
        useFeedback().error("Erreur lors de la création de la famille de tags", "small");
      }
    },

    /** Update a tag family (name and/or catalog-filter visibility), then refresh families. */
    async updateTagFamily({
      id,
      name,
      catalogFilterVisible,
    }: {
      id: string;
      name?: string;
      catalogFilterVisible?: boolean;
    }) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TagsApi);
        const response = await apiInstance.updateTagFamily({
          tagFamilyId: id,
          updateTagFamilyDto: {
            tagFamilyName: name,
            catalogFilterVisible,
          },
        });
        await this.getFamiliesTag();
        return response;
      } catch {
        useFeedback().error("Erreur lors de la mise à jour de la famille de tags", "small");
      }
    },

    /** Persist training tags for the current (or given) company, then refresh them. */
    async postTrainingTags(companyId?: string) {
      const companiesStore = useCreateCompaniesStore();
      if (companiesStore.company.companyId !== undefined || companyId !== undefined) {
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TagsApi);
          const trainingTag = await apiInstance.setCompanyTags({
            companyId: companiesStore.company.companyId ?? (companyId as string),
            category: TagCategoryEnum.TRAINING_TAG,
            setCompanyTagsDto: this.trainingTags,
          });
          await this.getTrainingTags({
            companyId: companiesStore.company.companyId ?? (companyId as string),
            nullable: true,
          });
          return { trainingTag };
        } catch {
          useFeedback().error("Erreur lors de l'enregistrement des tags de formation", "small");
        }
      }
    },

    /** Persist session tags for the current (or given) company, then refresh them. */
    async postSessionTags(companyId?: string) {
      const companiesStore = useCreateCompaniesStore();
      if (companiesStore.company.companyId !== undefined || companyId !== undefined) {
        try {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TagsApi);
          const sessionTag = await apiInstance.setCompanyTags({
            companyId: companiesStore.company.companyId ?? (companyId as string),
            category: TagCategoryEnum.SESSION_TAG,
            setCompanyTagsDto: this.sessionTags,
          });
          await this.getSessionTags();
          return { sessionTag };
        } catch {
          useFeedback().error("Erreur lors de l'enregistrement des tags de session", "small");
        }
      }
    },
  },
});
